import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "@/contexts/AuthContext";

/**
 * Appearance for the signed-in surfaces (podcaster portal + admin).
 *
 * Three looks, applied as a single root class:
 *   mixed ("mixed", no class) — black top bar/sidebar, light pages and cards
 *   light ("theme-light")     — nav goes light too
 *   dark  ("dark", default)   — everything dark
 *
 * Marketing pages keep their designed look, so the provider only wraps the
 * portal/admin layouts and clears the class again when it unmounts.
 */
export type ThemeChoice = "mixed" | "light" | "dark";
/** Which background the page content is painted on (drives logo selection). */
export type ResolvedTheme = "light" | "dark";

/** Pre-hydration hint only — the profile copy is the source of truth. */
const STORAGE_KEY = "podgo_theme";

const ROOT_CLASS: Record<ThemeChoice, string | null> = {
  mixed: null,
  light: "theme-light",
  dark: "dark",
};

const ALL_ROOT_CLASSES = ["theme-light", "dark"];

interface PortalThemeValue {
  /** What the user picked. */
  choice: ThemeChoice;
  /** Background the page content is painted on: mixed and light are both light. */
  resolved: ResolvedTheme;
  setChoice: (choice: ThemeChoice) => void;
}

const PortalThemeContext = createContext<PortalThemeValue | null>(null);

function isChoice(value: unknown): value is ThemeChoice {
  return value === "mixed" || value === "light" || value === "dark";
}

/** House default when the user has never picked a look. */
const DEFAULT_CHOICE: ThemeChoice = "dark";

function readStoredChoice(): ThemeChoice {
  if (typeof window === "undefined") return DEFAULT_CHOICE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  // Legacy "system" values (and anything unrecognised) fall back to the house default.
  return isChoice(stored) ? stored : DEFAULT_CHOICE;
}

export function PortalThemeProvider({ children }: { children: ReactNode }) {
  const { profile, updateProfile } = useAuth();
  const [choice, setChoiceState] = useState<ThemeChoice>(readStoredChoice);

  // The saved preference follows the user across devices, so it wins over the
  // local hint once the profile has loaded.
  const savedChoice = profile?.ui_prefs?.theme;
  useEffect(() => {
    if (isChoice(savedChoice)) {
      setChoiceState(savedChoice);
      window.localStorage.setItem(STORAGE_KEY, savedChoice);
    }
  }, [savedChoice]);

  const resolved: ResolvedTheme = choice === "dark" ? "dark" : "light";

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove(...ALL_ROOT_CLASSES);
    const next = ROOT_CLASS[choice];
    if (next) root.classList.add(next);
    root.style.colorScheme = resolved;
    return () => {
      root.classList.remove(...ALL_ROOT_CLASSES);
      root.style.colorScheme = "";
    };
  }, [choice, resolved]);

  const setChoice = useCallback(
    (next: ThemeChoice) => {
      setChoiceState(next);
      window.localStorage.setItem(STORAGE_KEY, next);
      if (!profile) return;
      void updateProfile({ ui_prefs: { ...(profile.ui_prefs ?? {}), theme: next } });
    },
    [profile, updateProfile],
  );

  const value = useMemo<PortalThemeValue>(
    () => ({ choice, resolved, setChoice }),
    [choice, resolved, setChoice],
  );

  return <PortalThemeContext.Provider value={value}>{children}</PortalThemeContext.Provider>;
}

/** Null outside the portal/admin layouts (i.e. on marketing pages). */
export function usePortalTheme() {
  return useContext(PortalThemeContext);
}

/** Background actually painted at this point in the tree. Marketing = light. */
export function useResolvedTheme(): ResolvedTheme {
  return useContext(PortalThemeContext)?.resolved ?? "light";
}
